import { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState({ status: "polling", tries: 0, data: null });

  useEffect(() => {
    const sid = new URLSearchParams(loc.search).get("session_id");
    if (!sid) { setState({ status: "error", data: { detail: "No session id" } }); return; }
    let cancelled = false;
    const poll = async (tries) => {
      if (cancelled || tries > 10) { setState(s => ({ ...s, status: tries > 10 ? "timeout" : s.status })); return; }
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          await checkAuth();
          setState({ status: "paid", data: r.data });
          return;
        }
        if (r.data.payment_status === "failed" || r.data.payment_status === "expired") {
          setState({ status: "failed", data: r.data });
          return;
        }
      } catch { /* keep polling */ }
      setTimeout(() => poll(tries + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; };
  }, [loc.search, checkAuth]);

  return (
    <div className="min-h-screen bg-[#070B14] text-white flex items-center justify-center p-8" data-testid="payment-success">
      <div className="max-w-lg w-full bg-[#0D1424] border border-white/10 p-10 text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-14 h-14 text-cyan-400 mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <div className="mono-label text-cyan-400 mb-3">// CONFIRMING PAYMENT</div>
            <div className="text-slate-300">Waiting for Stripe to confirm the transaction…</div>
          </>
        )}
        {state.status === "paid" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-cyan-400 mx-auto mb-6" strokeWidth={1.5} />
            <div className="mono-label text-cyan-400 mb-3">// PAYMENT CONFIRMED</div>
            <h1 className="font-display font-black text-3xl mb-3 tracking-tight">Welcome to {state.data?.plan}.</h1>
            <p className="text-slate-400 mb-8">Your subscription is active. All features have been unlocked.</p>
            <button onClick={() => nav("/dashboard")} className="bg-cyan-400 text-[#070B14] px-6 py-3 mono-label hover:bg-cyan-300" data-testid="to-dashboard">GO TO COMMAND CENTER</button>
          </>
        )}
        {(state.status === "failed" || state.status === "timeout") && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-6" strokeWidth={1.5} />
            <div className="mono-label text-red-400 mb-3">// PAYMENT ISSUE</div>
            <p className="text-slate-400 mb-8">{state.status === "failed" ? "Payment did not complete." : "Confirmation is taking longer than expected — check your billing dashboard."}</p>
            <Link to="/pricing" className="mono-label text-cyan-400 hover:text-cyan-300">TRY AGAIN</Link>
          </>
        )}
      </div>
    </div>
  );
}
